import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, AlertTriangle, Loader2 } from 'lucide-react';
import { logError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UnsubscribedPage() {
  const [params] = useSearchParams();
  const status = params.get('status');
  const token = params.get('token') || '';

  const [confirming, setConfirming] = useState(false);
  const [state, setState] = useState(() => {
    if (status === 'invalid') return 'invalid';
    if (status === 'ok') return 'done';
    if (token) return 'awaiting_confirm';
    return 'invalid';
  });

  const confirmUnsubscribe = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/emails/unsubscribe`, { token });
      // Endpoint always returns success (privacy: never leak whether the
      // token was valid). If we got here we redirected from a valid GET,
      // so the flag has been flipped.
      setState('done');
    } catch (err) {
      logError('unsubscribe confirm failed:', err?.response?.status);
      toast.error('Something went wrong — please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const isInvalid = state === 'invalid';
  const isDone = state === 'done';
  const isAwaiting = state === 'awaiting_confirm';

  const iconVariant = isInvalid
    ? { bg: 'bg-amber-50 border-amber-200', color: 'text-amber-600', Icon: AlertTriangle }
    : isDone
      ? { bg: 'bg-emerald-50 border-emerald-200', color: 'text-emerald-600', Icon: Check }
      : { bg: 'bg-stone-100 border-stone-200', color: 'text-stone-500', Icon: AlertTriangle };
  const StatusIcon = iconVariant.Icon;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" style={{ backgroundColor: '#F5F1E8' }}>
      <Card className="w-full max-w-md p-8 text-center bg-white" data-testid="unsubscribed-page">
        <div className="flex justify-center mb-6">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center border ${iconVariant.bg}`}>
            <StatusIcon className={`w-7 h-7 ${iconVariant.color}`} />
          </div>
        </div>

        <h1
          className="text-3xl mb-4"
          style={{ fontFamily: 'Playfair Display, serif', color: '#3A3833' }}
          data-testid="unsubscribed-heading"
        >
          {isInvalid && 'That link doesn’t look right'}
          {isDone && 'You’ve been unsubscribed'}
          {isAwaiting && 'Unsubscribe from marketing emails?'}
        </h1>

        {isInvalid && (
          <p className="text-stone-600 mb-6 leading-relaxed">
            Your unsubscribe link may have expired or been mistyped. If you keep getting emails you don’t want, reply to any of them and we’ll take care of it.
          </p>
        )}

        {isDone && (
          <>
            <p className="text-stone-600 mb-6 leading-relaxed">
              You won’t receive any more marketing or daily-reflection emails from us. Security emails (password resets, login codes, trial-ending reminders) will still be sent — those are required for your account safety.
            </p>
            <p className="text-sm text-stone-500 mb-8">
              Changed your mind? You can turn marketing emails back on anytime from your{' '}
              <Link to="/settings" className="underline hover:text-stone-700">Settings</Link> page.
            </p>
          </>
        )}

        {isAwaiting && (
          <>
            <p className="text-stone-600 mb-6 leading-relaxed">
              We’ll stop sending marketing and daily-reflection emails. Security emails (password resets, login codes, trial reminders) will still come through — those are required.
            </p>
            <Button
              onClick={confirmUnsubscribe}
              disabled={confirming}
              className="rounded-full px-6 mb-3"
              data-testid="unsubscribe-confirm-button"
            >
              {confirming ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Unsubscribing…</>
              ) : (
                'Confirm unsubscribe'
              )}
            </Button>
            <p className="text-xs text-stone-400 mb-6">
              Changed your mind? You can safely close this tab — we won’t unsubscribe you unless you click above.
            </p>
          </>
        )}

        <Link to="/">
          <Button variant="outline" className="rounded-full" data-testid="unsubscribed-home-button">
            Return home
          </Button>
        </Link>
      </Card>
    </div>
  );
}
